import React, { useEffect, useState } from 'react';
import { billingService } from '../../services/billingService';
import { PLAN_DESCRIPTIONS, PLAN_FEATURES } from './planMarketing';
import './PricingPage.css';
import StateBlock from '../common/StateBlock';
import { getUserFacingError } from '../../lib/userFacingErrors';

const TIER_ORDER = ['starter', 'growth', 'pro'];
const TRUST_ITEMS = [
  'Stripe-secured checkout',
  'Transparent usage-based overages',
  'Cancel anytime',
];

export default function PricingPage({ onSelectPlan, selectedTier, hideTrust = false }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        setError(null);
        const data = await billingService.getPlans();
        data.sort((a, b) => TIER_ORDER.indexOf(a.tier) - TIER_ORDER.indexOf(b.tier));
        setPlans(data);
      } catch (e) {
        console.error('Failed to load plans:', e);
        setError(getUserFacingError(e, 'billing-load'));
      } finally {
        setLoading(false);
      }
    })();
  }, [retryKey]);

  if (loading) {
    return <div className="pricing-loading">Loading plans...</div>;
  }

  if (error || plans.length === 0) {
    return (
      <StateBlock
        state="error"
        title="Plans aren’t available right now"
        message={error?.message || 'We couldn’t load plan options. Please try again.'}
        actionLabel={error?.actionLabel || 'Retry'}
        onAction={() => {
          setLoading(true);
          setRetryKey((value) => value + 1);
        }}
      />
    );
  }

  return (
    <div className="pricing-page">
      <div className="pricing-header">
      </div>
      <div className="pricing-grid">
        {plans.map((plan) => {
          const isPopular = plan.tier === 'growth';
          const isSelected = selectedTier === plan.tier;
          const priceWhole = Math.floor(plan.price_cents / 100);
          const priceCents = (plan.price_cents % 100).toString().padStart(2, '0');

          return (
            <div
              key={plan.tier}
              className={`pricing-card tier-${plan.tier}${isPopular ? ' popular' : ''}${isSelected ? ' selected' : ''}`}
            >
              <div className="plan-header">
                <h3 className="plan-name">{plan.name}</h3>
                <div className="plan-price-row">
                  <span className="plan-price-main">
                    ${priceWhole}
                    {priceCents !== '00' && <span className="plan-price-cents">.{priceCents}</span>}
                    <span className="plan-price-unit">/ Month</span>
                  </span>
                </div>
                <div className="plan-description">{PLAN_DESCRIPTIONS[plan.tier]}</div>
              </div>

              <div className="plan-divider" />

              <ul className="plan-features">
                {(PLAN_FEATURES[plan.tier] || plan.features).map((f, i) => (
                  <li key={i}>
                    <span className="feature-check" aria-hidden="true">✓</span>
                    {f}
                  </li>
                ))}
              </ul>

              <button
                className={`plan-select-btn${isPopular ? ' popular' : ''}${isSelected ? ' selected' : ''}`}
                onClick={() => onSelectPlan(plan)}
                disabled={isSelected}
              >
                {isSelected ? '✓ Selected' : 'Select'}
              </button>
            </div>
          );
        })}
      </div>

      {!hideTrust && (
        <div className="pricing-trust">
          {TRUST_ITEMS.map((item) => (
            <span className="trust-item" key={item}>
              <span className="trust-check" aria-hidden="true">✓</span>
              {item}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
